import { TFunction } from 'i18next'
import { useState } from 'react'

import { awaitPendingJobs, displayErrorToast } from '../helpers'
import { getEntityFromBlizzard } from '../api/search'
import { useQueryClient } from '@tanstack/react-query'
import useBasePath from '../hooks/useBasePath'
import { useGameVersion } from './useGameVersion'

export const useBlizzardApi = <TBlizzardEntity>(
  type: 'character' | 'guild',
  t: TFunction,
) => {
  const basePath = useBasePath(type)
  const queryClient = useQueryClient()
  const gameVersion = useGameVersion()
  const [_1, _2, uriRegion, uriRealm, uriName] =
    window.location.pathname.split('/')

  const [blizzardInfo, setBlizzardInfo] = useState<TBlizzardEntity>()
  const [blizzardStatus, setBlizzardStatus] = useState<
    '' | 'pending' | 'not-found' | 'found'
  >('')

  const searchBlizzard = () => {
    if (!gameVersion.has('blizzard_api')) return

    setBlizzardStatus('pending')
    getEntityFromBlizzard<TBlizzardEntity>(uriName, uriRealm, uriRegion, type)
      .then(async ({ results, jobIds }) => {
        if (!results.length) {
          setBlizzardStatus('not-found')
          return
        }

        const statuses = await awaitPendingJobs(jobIds)
        const failed = statuses.find((status) => status.error)
        if (failed?.error) {
          displayErrorToast(failed.error, t)
          setBlizzardStatus('')
          return
        }

        setBlizzardInfo(results[0])
        queryClient.invalidateQueries({
          queryKey: [type, { path: basePath }],
        })
        setBlizzardStatus('found')
      })
      .catch((response) => {
        displayErrorToast(response, t)
        setBlizzardStatus('')
      })
  }

  return {
    uriRegion,
    uriRealm,
    uriName,
    blizzardInfo,
    blizzardStatus,
    searchBlizzard,
  }
}
